import React, { useState, useEffect } from 'react';
import { PayPalButtons } from "@paypal/react-paypal-js";
import { usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { sendNotificationEmails } from './emailUtils';
import { Box } from "@mui/material";
import { getAuth, createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from 'firebase/auth';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { doc, setDoc, collection, query, where, getDocs, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import CircularProgress from "@mui/material/CircularProgress";
import ReCAPTCHA from "react-google-recaptcha";
import { getActivePromotion } from "./promotions";
import { useTheme } from '@mui/material/styles';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import StripeButton from './StripeButton';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export const MEMBERSHIP_PLANS = {
  ANNUAL: {
    id: 'annual_plan',
    price: '190',
    name: 'Annual Membership',
    paypalPlanId: 'P-9C343039VK386691UM5MEJNY',
    stripePriceId: 'price_1RTOSwJeXHx1yCsahxJZgcC4', // Replace with your Stripe Price ID
    discountedPlans: {
      10: { paypal: 'P-6RE59828SP869803AM5MEJ7A', stripe: 'price_1Rs1DNJeXHx1yCsareATkYWs'  },
      20: { paypal: 'P-0KM08602J63052631M5MEKPI', stripe: 'price_1Rs1EHJeXHx1yCsaMDcBpMNM' },
      50: { paypal: 'P-1G573204282888046M7EGXCQ', stripe: 'price_annual_50_discount' },
    }
  },
  MONTHLY: {
    id: 'monthly_plan',
    price: '19.99',
    name: 'Monthly Membership',
    paypalPlanId: 'P-2NK59470NU863551SM5MG2KQ',
    stripePriceId: 'price_1RTOPxJeXHx1yCsa8qK948P7', // Replace with your Stripe Price ID
    discountedPlans: {
      10: { paypal: 'P-81S05014539389443M5MEMZY', stripe: 'price_monthly_10_discount' },
      15: { paypal: 'P-4HR79237635486827M5MENEI', stripe: 'price_1RrGH2JeXHx1yCsaSBKj6c7k' },
      20: { paypal: 'P-7RF08193D7094774BM5MENOY', stripe: 'price_1RrGFfJeXHx1yCsa3niMeUuY' },
      40: { paypal: 'P-04758357GE1194720M7PP5DQ', stripe: 'price_monthly_40_discount' },
      50: { paypal: 'P-87344122JX139871JM7EGW7Y', stripe: 'price_monthly_50_discount' },
      99: { paypal: 'P-1VR76201HW1726152M5MEOMY', stripe: 'price_1Rs1rAJeXHx1yCsasoWzN8e2' }
    }
  },
  TRIAL: {
    id: 'trial_plan',
    price: '0',
    name: '7-Day Trial',
    duration: 7,
    paypalPlanId: 'P-9JT12961S1563312DM5MG2PY',
    stripePriceId: 'price_trial_plan', // Replace with your Stripe Price ID
    trialDays: 7,
    regularPlanId: 'monthly_plan'
  },
  TRIAL_UPGRADE: {
    id: 'trial_upgrade_plan',
    price: '5',
    regularPrice: '19.99',
    name: 'Trial Upgrade Special',
    description: 'Special offer: $5 for your first month, then $19.99/month',
    paypalPlanId: 'P-8CD823480S358202PM7IFTCY',
    stripePriceId: 'price_1RTOUuJeXHx1yCsaDtYVF8PP', // Replace with your Stripe Price ID
    duration: 30
  },
  DEMO: {
    id: 'demo_plan',
    price: '0',
    name: 'Demo Account',
    duration: 999
  }
};

export const getDiscountedPrice = (originalPrice, planId, discountInfo, activePromotion) => {
  console.log('Calculating discounted price:', { originalPrice, planId, discountInfo, activePromotion });

  if (discountInfo &&
    (discountInfo.plans.includes(planId) ||
      discountInfo.plans.includes('all_plans') ||
      (planId === 'monthly_plan' && discountInfo.isTrialUpgrade))) {
    if (discountInfo.isTrialUpgrade) {
      console.log('Using Trial Upgrade price:', discountInfo.trialUpgradePrice);
      return discountInfo.trialUpgradePrice;
    }

    if (discountInfo.type === 'percentage') {
      const discountedPrice = (originalPrice * (100 - discountInfo.value) / 100).toFixed(2);
      console.log('Calculated code discounted price:', discountedPrice);
      return discountedPrice;
    }

    const discountedPrice = Math.max(0, originalPrice - discountInfo.value).toFixed(2);
    console.log('Calculated code discounted price:', discountedPrice);
    return discountedPrice;
  }

  if (activePromotion && activePromotion.appliesTo.includes(planId)) {
    const discountedPrice = (originalPrice * (100 - activePromotion.discountValue) / 100).toFixed(2);
    console.log('Calculated promotion discounted price:', discountedPrice);
    return discountedPrice;
  }

  return originalPrice;
};

function SignUpPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [debouncedPassword, setDebouncedPassword] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [debouncedFullName, setDebouncedFullName] = useState('');
  const [{ isResolved, isPending, isRejected }] = usePayPalScriptReducer();
  const [passwordError, setPasswordError] = useState('');
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [activePromotion, setActivePromotion] = useState(null);
  const [isSpecialOffer, setIsSpecialOffer] = useState(false);
  const [specialOfferType, setSpecialOfferType] = useState(null);
  const [specialOfferEmail, setSpecialOfferEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [pendingTeamPermission, setPendingTeamPermission] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const upgradeType = params.get('upgrade');
    const special = params.get('special');
    const emailParam = params.get('email');

    console.log('Checking params:', { upgradeType, special, emailParam });

    if (upgradeType === 'demo') {
      setIsUpgrade(true);
      const plansSection = document.getElementById('plans-section');
      if (plansSection) {
        plansSection.scrollIntoView({ behavior: 'smooth' });
      }
    }

    if (special === 'trial_upgrade' && emailParam) {
      setEmail(emailParam);
      setSpecialOfferEmail(emailParam);
      setIsSpecialOffer(true);
      setSpecialOfferType('trial_upgrade');
      const specialSection = document.getElementById('special-offer-section');
      if (specialSection) {
        setTimeout(() => {
          specialSection.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
  }, [location]);

  useEffect(() => {
    const promo = getActivePromotion();
    if (promo) {
      console.log('Active promotion found:', promo.name);
      setActivePromotion(promo);
    }
  }, []);

  useEffect(() => {
    const permissionData = sessionStorage.getItem('pendingTeamPermission');
    if (permissionData) {
      setPendingTeamPermission(JSON.parse(permissionData));
      setShowPermissionModal(true);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmail(email);
    }, 500);
    return () => clearTimeout(timer);
  }, [email]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPassword(password);
    }, 500);
    return () => clearTimeout(timer);
  }, [password]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFullName(fullName);
    }, 500);
    return () => clearTimeout(timer);
  }, [fullName]);

  const validateDiscountCode = async (code) => {
    try {
      setValidatingCode(true);
      const codeRef = await getDocs(
        query(
          collection(db, 'discountCodes'),
          where('code', '==', code.toUpperCase()),
          where('active', '==', true)
        )
      );
      if (!codeRef.empty) {
        const codeData = codeRef.docs[0].data();
        if (codeData.currentUses >= codeData.maxUses) {
          setError('This discount code has reached its maximum uses');
          return false;
        }
        if (codeData.plans.includes('all_plans') || codeData.plans.includes('monthly_plan') || codeData.plans.includes('annual_plan') || codeData.plans.includes('trial_upgrade_plan')) {
          if (codeData.type === 'percentage') {
            const value = parseInt(codeData.value);
            if (codeData.plans.includes('monthly_plan') || codeData.plans.includes('all_plans')) {
              codeData.monthlyPlanId = MEMBERSHIP_PLANS.MONTHLY.discountedPlans[value]?.paypal;
              codeData.stripeMonthlyPriceId = MEMBERSHIP_PLANS.MONTHLY.discountedPlans[value]?.stripe;
              if (value === 99) codeData.isTest = true;
              if (value === 100) {
                codeData.isGift = true;
                codeData.duration = code.toUpperCase().includes('3MONTH') ? 90 : 30;
                codeData.convertToDemo = code.toUpperCase().includes('3MONTH');
              }
            }
            if (codeData.plans.includes('annual_plan') || codeData.plans.includes('all_plans')) {
              codeData.annualPlanId = MEMBERSHIP_PLANS.ANNUAL.discountedPlans[value]?.paypal;
              codeData.stripeAnnualPriceId = MEMBERSHIP_PLANS.ANNUAL.discountedPlans[value]?.stripe;
              if (value === 100) {
                codeData.isGift = true;
                codeData.duration = code.toUpperCase().includes('3MONTH') ? 90 : 365;
                codeData.convertToDemo = code.toUpperCase().includes('3MONTH');
              }
            }
            if (codeData.plans.includes('trial_upgrade_plan') || codeData.plans.includes('all_plans')) {
              codeData.monthlyPlanId = MEMBERSHIP_PLANS.TRIAL_UPGRADE.paypalPlanId;
              codeData.stripeMonthlyPriceId = MEMBERSHIP_PLANS.TRIAL_UPGRADE.stripePriceId;
              codeData.isTrialUpgrade = true;
              codeData.trialUpgradePrice = MEMBERSHIP_PLANS.TRIAL_UPGRADE.price;
            }
          }
          codeData.teamId = codeData.teamId || null;
          setDiscountInfo(codeData);
          return true;
        }
      }
      setDiscountInfo(null);
      setError('Invalid discount code');
      return false;
    } catch (error) {
      console.error('Error validating code:', error);
      setError('Error validating discount code');
      return false;
    } finally {
      setValidatingCode(false);
    }
  };

  const getPlanId = (basePlan, discountInfoParam) => {
    console.log('Getting plan ID with:', { basePlan, discountInfoParam, activePromotion });

    if (discountInfoParam && discountInfoParam.type === 'percentage') {
      if (paymentMethod === 'paypal') {
        const planId = basePlan.id === 'monthly_plan' ?
          discountInfoParam.monthlyPlanId :
          discountInfoParam.annualPlanId;
        console.log('Using PayPal discount code plan ID:', planId);
        return planId;
      } else if (paymentMethod === 'stripe') {
        const priceId = basePlan.id === 'monthly_plan'
          ? discountInfoParam.stripeMonthlyPriceId || MEMBERSHIP_PLANS.MONTHLY.discountedPlans[discountInfoParam.value]?.stripe
          : discountInfoParam.stripeAnnualPriceId || MEMBERSHIP_PLANS.ANNUAL.discountedPlans[discountInfoParam.value]?.stripe;
        console.log('Selected Stripe discount code price ID:', priceId);
        if (!priceId) {
          console.error('No valid Stripe price ID found for:', { basePlan, discountInfoParam });
        }
        return priceId || basePlan.stripePriceId;
      }
    }

    if (activePromotion && activePromotion.appliesTo.includes(basePlan.id)) {
      const promoPercentage = activePromotion.discountValue;
      if (paymentMethod === 'paypal') {
        const planId = basePlan.discountedPlans[promoPercentage]?.paypal;
        if (planId) {
          console.log('Using PayPal promotion plan ID:', planId);
          return planId;
        }
      } else if (paymentMethod === 'stripe') {
        const priceId = basePlan.discountedPlans[promoPercentage]?.stripe;
        if (priceId) {
          console.log('Using Stripe promotion price ID:', priceId);
          return priceId;
        }
      }
    }

    if (paymentMethod === 'paypal') {
      console.log('Using default PayPal plan ID:', basePlan.paypalPlanId);
      return basePlan.paypalPlanId;
    } else if (paymentMethod === 'stripe') {
      console.log('Using default Stripe price ID:', basePlan.stripePriceId);
      return basePlan.stripePriceId;
    }
  };

  const handleFreeSubscription = async (plan) => {
    try {
      setLoading(true);
      setError(null);
  
      if (!email || !email.includes('@')) {
        setError('Valid email is required');
        return;
      }
      const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
      if (!password || !passwordRegex.test(password)) {
        setError('Password must be at least 8 characters and include at least one number and one special character (e.g., !@#$%^&*)');
        return;
      }
      if (!fullName) {
        setError('Full name is required');
        return;
      }
      const auth = getAuth();
      try {
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.length > 0) {
          setError('Email already registered. Please sign in or use a different email.');
          return;
        }
      } catch (error) {
        if (error.code === 'auth/email-already-in-use') {
          setError('Email already registered. Please sign in or use a different email.');
          return;
        }
        throw error;
      }
      const result = await createUserWithEmailAndPassword(auth, email, password);

      const userDocData = {
        fullName,
        email,
        plan: plan.id,
        discountCode: discountCode,
        createdAt: new Date().toISOString(),
        subscriptionStatus: 'active',
        giftedAccount: true
      };

      if (discountInfo?.teamId) {
        userDocData.profileInfo = {
          role: 'member',
          teamId: discountInfo.teamId,
          teamRole: 'member',
          teamPermissions: { [discountInfo.teamId]: false }
        };
        await updateDoc(doc(db, 'teams', discountInfo.teamId), {
          members: arrayUnion(result.user.uid)
        });
        sessionStorage.setItem('pendingTeamPermission', JSON.stringify({
          userId: result.user.uid,
          teamId: discountInfo.teamId
        }));
      }

      await setDoc(doc(db, 'Users', result.user.uid), userDocData);

      await sendNotificationEmails({
        fullName,
        email,
        plan: 'Gift Account',
        isGifted: true,
        discountCode
      }, {
        plan: 'Gift Account',
        cost: '0',
        nextBillingDate: new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString()
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Free signup failed:', error);
      if (error.code === 'auth/weak-password') {
        setError('Password does not meet requirements: must include at least one number and one special character');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDemoSignup = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!email || !email.includes('@')) {
        setError('Valid email is required');
        return;
      }
      const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
      if (!password || !passwordRegex.test(password)) {
        setError('Password must be at least 8 characters and include at least one number and one special character (e.g., !@#$%^&*)');
        return;
      }
      if (!fullName) {
        setError('Full name is required');
        return;
      }
      const auth = getAuth();
      try {
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.length > 0) {
          setError('Email already registered. Please sign in or use a different email.');
          return;
        }
      } catch (error) {
        if (error.code === 'auth/email-already-in-use') {
          setError('Email already registered. Please sign in or use a different email.');
          return;
        }
        throw error;
      }
      const result = await createUserWithEmailAndPassword(auth, email, password);

      const demoUID = `DEMO_${result.user.uid}`;
      const demoEmail = `DEMO_${email}`;

      const userDocData = {
        fullName,
        email: demoEmail,
        originalEmail: email,
        plan: MEMBERSHIP_PLANS.DEMO.id,
        isDemo: true,
        demoUID: demoUID,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + (MEMBERSHIP_PLANS.DEMO.duration * 24 * 60 * 60 * 1000)).toISOString()
      };

      if (discountInfo?.teamId) {
        userDocData.profileInfo = {
          role: 'member',
          teamId: discountInfo.teamId,
          teamRole: 'member',
          teamPermissions: { [discountInfo.teamId]: false }
        };
        await updateDoc(doc(db, 'teams', discountInfo.teamId), {
          members: arrayUnion(result.user.uid)
        });
        sessionStorage.setItem('pendingTeamPermission', JSON.stringify({
          userId: result.user.uid,
          teamId: discountInfo.teamId
        }));
      }

      await setDoc(doc(db, 'Users', result.user.uid), userDocData);

      await setDoc(doc(db, 'subscriptions', result.user.uid), {
        userId: result.user.uid,
        type: 'demo',
        status: 'active',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + (MEMBERSHIP_PLANS.DEMO.duration * 24 * 60 * 60 * 1000)).toISOString(),
        plan: MEMBERSHIP_PLANS.DEMO.id
      });

      console.log('Sending demo signup notifications');
      await sendNotificationEmails(
        {
          fullName,
          email,
          plan: 'Demo Account',
          isTrial: false,
          isDemo: true
        },
        {
          plan: 'Demo Account',
          cost: '0',
          nextBillingDate: new Date(Date.now() + (MEMBERSHIP_PLANS.DEMO.duration * 24 * 60 * 60 * 1000)).toISOString()
        }
      );

      localStorage.setItem('userID', result.user.uid);
      navigate('/authentication/sign-in');
    } catch (error) {
      console.error('Demo signup failed:', error);
      if (error.code === 'auth/weak-password') {
        setError('Password does not meet requirements: must include at least one number and one special character');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTrialSignup = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!email || !email.includes('@')) {
        setError('Valid email is required');
        return;
      }
      const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
      if (!password || !passwordRegex.test(password)) {
        setError('Password must be at least 8 characters and include at least one number and one special character');
        return;
      }
      if (!fullName) {
        setError('Full name is required');
        return;
      }

      const auth = getAuth();

      try {
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.length > 0) {
          setError('Email already registered. Please sign in or use a different email.');
          return;
        }
      } catch (error) {
        if (error.code === 'auth/email-already-in-use') {
          setError('Email already registered. Please sign in or use a different email.');
          return;
        }
        throw error;
      }

      const result = await createUserWithEmailAndPassword(auth, email, password);
      const trialEndDate = new Date(Date.now() + (7 * 24 * 60 * 60 * 1000));

      const userDocData = {
        fullName,
        email,
        plan: MEMBERSHIP_PLANS.TRIAL.id,
        isTrial: true,
        convertToDemo: true,
        createdAt: new Date().toISOString(),
        trialEndDate: trialEndDate.toISOString(),
        expiresAt: trialEndDate.toISOString()
      };

      if (discountInfo?.teamId) {
        userDocData.profileInfo = {
          role: 'member',
          teamId: discountInfo.teamId,
          teamRole: 'member',
          teamPermissions: { [discountInfo.teamId]: false }
        };
        await updateDoc(doc(db, 'teams', discountInfo.teamId), {
          members: arrayUnion(result.user.uid)
        });
        sessionStorage.setItem('pendingTeamPermission', JSON.stringify({
          userId: result.user.uid,
          teamId: discountInfo.teamId
        }));
      }

      await setDoc(doc(db, 'Users', result.user.uid), userDocData);

      await setDoc(doc(db, 'subscriptions', result.user.uid), {
        userId: result.user.uid,
        type: 'trial',
        status: 'active',
        createdAt: new Date().toISOString(),
        trialEndDate: trialEndDate.toISOString(),
        convertToDemoAt: trialEndDate.toISOString(),
        expiresAt: trialEndDate.toISOString(),
        plan: MEMBERSHIP_PLANS.TRIAL.id,
        isTrial: true,
        convertToDemo: true,
        planDetails: {
          cost: 0,
          originalCost: MEMBERSHIP_PLANS.MONTHLY.price,
          nextBillingDate: trialEndDate.toISOString()
        }
      });

      await sendNotificationEmails(
        {
          fullName,
          email,
          plan: '7-Day Trial',
          isTrial: true
        },
        {
          plan: '7-Day Trial',
          cost: '0',
          nextBillingDate: trialEndDate.toISOString()
        }
      );

      localStorage.setItem('userID', result.user.uid);
      localStorage.setItem('isAuthenticated', 'true');
      navigate('/authentication/sign-in');
    } catch (error) {
      console.error('Trial signup failed:', error);
      if (error.code === 'auth/weak-password') {
        setError('Password does not meet requirements: must include at least one number and one special character');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubscription = async (plan) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedPlan(plan.id);

      if (discountInfo && parseFloat(getDiscountedPrice(plan.price, plan.id, discountInfo, activePromotion)) === 0) {
        return handleFreeSubscription(plan);
      }

      const finalPrice = discountInfo ? getDiscountedPrice(plan.price, plan.id, discountInfo, activePromotion) : plan.price;
      const payload = {
        plan_id: getPlanId(plan, discountInfo),
        payment_method: paymentMethod,
        ...(discountInfo && {
          discount_code: discountCode,
          discount_type: discountInfo.type,
          discount_value: discountInfo.value,
          final_price: finalPrice,
          is_trial_upgrade: discountInfo.isTrialUpgrade || false,
          teamId: discountInfo.teamId
        })
      };

      const endpoint = paymentMethod === 'stripe' ? '/api/create-stripe-checkout' : '/api/create-subscription';
      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.error) throw new Error(result.message);

      const pendingSignup = {
        email,
        password,
        fullName,
        plan: discountInfo?.isTrialUpgrade ? MEMBERSHIP_PLANS.TRIAL_UPGRADE.id : plan.id,
        discountCode: discountInfo ? discountCode : null,
        discountInfo: discountInfo || null,
        teamId: discountInfo?.teamId || null,
        paymentMethod,
        subscriptionID: paymentMethod === 'stripe' ? result.sessionId : result.subscriptionID
      };

      sessionStorage.setItem('pendingSignup', JSON.stringify(pendingSignup));

      if (discountInfo?.teamId && result.userId) {
        sessionStorage.setItem('pendingTeamPermission', JSON.stringify({
          userId: result.userId,
          teamId: discountInfo.teamId
        }));
      }

      if (paymentMethod === 'stripe') {
        const stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: result.sessionId });
      } else {
        window.location.href = result.approvalUrl;
      }
    } catch (error) {
      console.error('Subscription creation failed:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const PermissionModal = () => {
    const handlePermissionSubmit = async (allow) => {
      if (pendingTeamPermission) {
        try {
          await updateDoc(doc(db, 'Users', pendingTeamPermission.userId), {
            [`profile.teamPermissions.${pendingTeamPermission.teamId}`]: allow
          });
          console.log('Permission updated:', allow);
          sessionStorage.removeItem('pendingTeamPermission');
          setShowPermissionModal(false);
          setPendingTeamPermission(null);
        } catch (error) {
          console.error('Error updating permission:', error);
          setError('Failed to update permission. Please try again.');
        }
      }
    };

    return (
      <Dialog
        open={showPermissionModal}
        onClose={() => handlePermissionSubmit(false)}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.default,
          }
        }}
      >
        <DialogTitle>Team Data Permission</DialogTitle>
        <DialogContent>
          <MDTypography>
            Allow your coach to view your trade data for personalized guidance?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => handlePermissionSubmit(false)}>Deny</MDButton>
          <MDButton onClick={() => handlePermissionSubmit(true)} color="info">Allow</MDButton>
        </DialogActions>
      </Dialog>
    );
  };

  const isFormValid =
    email &&
    email.includes('@') &&
    password &&
    /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/.test(password) &&
    passwordConfirm &&
    password === passwordConfirm &&
    fullName &&
    captchaToken &&
    !loading;

  const testAPI = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/health`);
      const data = await response.json();
      console.log('API Health Check:', data);
    } catch (error) {
      console.error('API Connection Failed:', error);
    }
  };

  if (isPending) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </MDBox>
    );
  }

  if (isRejected) {
    console.error('PayPal script failed to load');
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <MDTypography>Failed to load payment system. Please try again later.</MDTypography>
      </MDBox>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        p={2}
      >
        <Card sx={{ maxWidth: '1200px', width: '100%', overflow: 'visible' }}>
          {activePromotion && (
            <MDBox
              width="100%"
              position="relative"
              sx={{
                background: 'linear-gradient(45deg, #ff9800 30%, #ff5722 90%)',
                boxShadow: '0 4px 20px rgba(255, 152, 0, 0.4)',
                borderRadius: { xs: 0, md: '8px 8px 0 0' },
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -15,
                  left: -15,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  zIndex: 0
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -30,
                  right: -20,
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  zIndex: 0
                }}
              />
              {[...Array(5)].map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    top: Math.random() * 70 + 10,
                    left: Math.random() * 80 + (i * 15),
                    color: 'white',
                    fontSize: '22px',
                    opacity: 0.8 + Math.random() * 0.2,
                    animation: `sparkle ${1 + Math.random()}s infinite ease-in-out alternate`
                  }}
                >
                  ★
                </Box>
              ))}
              <MDBox
                p={3}
                position="relative"
                zIndex={2}
                textAlign="center"
                sx={{
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1" fill-rule="evenodd"%3E%3Cpath d="M0 40L40 0H20L0 20M40 40V20L20 40"/%3E%3C/g%3E%3C/svg%3E")',
                    zIndex: -1
                  }
                }}
              >
                <MDBox
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                >
                  <MDBox
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '28px',
                      fontWeight: 'bold'
                    }}
                  >
                    50%
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="h5" fontWeight="bold" color="white">
                      {activePromotion.name}: {activePromotion.description}
                    </MDTypography>
                    <MDTypography variant="body2" color="white" mt={1}>
                      Limited time offer valid through {new Date(activePromotion.endDate).toLocaleDateString()}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
              <style jsx global>{`
                @keyframes sparkle {
                  0% { transform: scale(1); }
                  100% { transform: scale(1.3); }
                `}
              </style>
            </MDBox>
          )}
          <MDBox p={3}>
            <MDBox textAlign="center" mb={3}>
              <MDTypography variant="h3" fontWeight="bold">
                {isUpgrade ? 'Upgrade Your Account' : 'Join FX Bullsheet'}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {isUpgrade ? 'Choose your premium plan to unlock all features' : 'Choose your plan and start trading smarter'}
              </MDTypography>
            </MDBox>
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <MDBox
                  p={3}
                  component={Card}
                  sx={{ boxShadow: 'none', border: '1px solid #eee' }}
                >
                  <form onSubmit={(e) => e.preventDefault()}>
                    <MDBox mb={2}>
                      <MDTypography variant="h6" mb={1}>
                        Account Details
                      </MDTypography>
                      <MDBox mb={2}>
                        <MDTypography variant="h6">Select Payment Method</MDTypography>
                        <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                        <FormControlLabel value="stripe" control={<Radio />} label="Stripe" />
                          <FormControlLabel value="paypal" control={<Radio />} label="PayPal" />

                        </RadioGroup>
                      </MDBox>
                      <MDInput
                        label="Full Name"
                        fullWidth
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        disabled={loading}
                        sx={{ mb: 2 }}
                      />
                      <MDInput
                        label="Email"
                        type="email"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                        sx={{ mb: 2 }}
                      />
                      <MDInput
                        label="Password"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => {
                          const newPassword = e.target.value;
                          setPassword(newPassword);
                          const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
                          if (newPassword.length < 8) {
                            setPasswordError('Password must be at least 8 characters');
                          } else if (!passwordRegex.test(newPassword)) {
                            setPasswordError('Password must include at least one number and one special character (e.g., !@#$%^&*)');
                          } else if (passwordConfirm && newPassword !== passwordConfirm) {
                            setPasswordError('Passwords do not match');
                          } else {
                            setPasswordError('');
                          }
                        }}
                        disabled={loading}
                        error={!!passwordError && password.length > 0}
                        helperText={passwordError || (password.length > 0 && password.length < 8 ? 'Password must be at least 8 characters' : '')}
                        sx={{ mb: 1 }}
                      />
                      <MDInput
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        value={passwordConfirm}
                        onChange={(e) => {
                          const newPasswordConfirm = e.target.value;
                          setPasswordConfirm(newPasswordConfirm);
                          const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
                          if (password && newPasswordConfirm !== password) {
                            setPasswordError('Passwords do not match');
                          } else if (password.length < 8) {
                            setPasswordError('Password must be at least 8 characters');
                          } else if (!passwordRegex.test(password)) {
                            setPasswordError('Password must include at least one number and one special character (e.g., !@#$%^&*)');
                          } else {
                            setPasswordError('');
                          }
                        }}
                        disabled={loading}
                        error={!!passwordError}
                        helperText={passwordError}
                        sx={{ mb: 2 }}
                      />
                      <MDTypography variant="caption" color="text" mt={1} display="block">
                        Password must be at least 8 characters and include at least one number and one special character (e.g., !@#$%^&*).
                      </MDTypography>
                      <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(token) => setCaptchaToken(token)}
                        onExpired={() => setCaptchaToken(null)}
                      />
                    </MDBox>
                    <MDBox mb={3}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDInput
                          label="Discount Code"
                          fullWidth
                          value={discountCode}
                          onChange={(e) => {
                            setDiscountCode(e.target.value.toUpperCase());
                            setError('');
                            if (!e.target.value) {
                              setDiscountInfo(null);
                            }
                          }}
                          disabled={loading || validatingCode}
                          error={!!error}
                        />
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={() => validateDiscountCode(discountCode)}
                          disabled={!discountCode || loading || validatingCode}
                          sx={{ minWidth: '100px' }}
                        >
                          {validatingCode ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            'Apply'
                          )}
                        </MDButton>
                      </MDBox>
                      {discountInfo && (
                        <MDTypography variant="caption" color="success" mt={1} display="block">
                          {discountInfo.type === 'percentage'
                            ? `${discountInfo.value}% discount applied!`
                            : `$${discountInfo.value} discount applied!`}
                        </MDTypography>
                      )}
                      {error && (
                        <MDTypography color="error" variant="caption" mt={1} display="block">
                          {error}
                        </MDTypography>
                      )}
                    </MDBox>
                  </form>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={7}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <MDTypography variant="h6" mb={2}>
                      {isUpgrade ? 'Choose Your Premium Plan' : 'Premium Plans'}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      height: '100%',
                      boxShadow: '0 2px 12px 0 rgba(0,0,0,0.1)',
                      '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' }
                    }}>
                      <MDBox p={2}>
                        <MDTypography variant="h6" fontWeight="medium">
                          {MEMBERSHIP_PLANS.ANNUAL.name}
                        </MDTypography>
                        <MDTypography variant="caption" color="success" fontWeight="medium" mb={2}>
                          Save over 20% compared to monthly plan!
                        </MDTypography>
                        <MDTypography variant="h4" my={1}>
                          {(discountInfo && (discountInfo.plans.includes(MEMBERSHIP_PLANS.ANNUAL.id) ||
                            discountInfo.plans.includes('all_plans'))) ||
                            (activePromotion && activePromotion.appliesTo.includes(MEMBERSHIP_PLANS.ANNUAL.id)) ? (
                            <>
                              <MDTypography
                                component="span"
                                variant="h4"
                                color="text"
                                sx={{ textDecoration: 'line-through', opacity: 0.5, mr: 1 }}
                              >
                                ${MEMBERSHIP_PLANS.ANNUAL.price}
                              </MDTypography>
                              ${getDiscountedPrice(MEMBERSHIP_PLANS.ANNUAL.price, MEMBERSHIP_PLANS.ANNUAL.id, discountInfo, activePromotion)}
                            </>
                          ) : (
                            `$${MEMBERSHIP_PLANS.ANNUAL.price}`
                          )}
                          <MDTypography variant="caption" ml={0.5}>/year</MDTypography>
                        </MDTypography>
                        {discountInfo && parseFloat(getDiscountedPrice(MEMBERSHIP_PLANS.ANNUAL.price, MEMBERSHIP_PLANS.ANNUAL.id, discountInfo, activePromotion)) === 0 ? (
                          <MDButton
                            variant="gradient"
                            color="success"
                            fullWidth
                            onClick={() => handleFreeSubscription(MEMBERSHIP_PLANS.ANNUAL)}
                            disabled={!isFormValid || loading}
                          >
                            {loading ? <CircularProgress size={20} color="inherit" /> : "Create Gifted Account"}
                          </MDButton>
                        ) : paymentMethod === 'paypal' ? (
                          <PayPalButtons
                            forceReRender={[discountInfo, debouncedEmail, debouncedPassword, debouncedFullName, paymentMethod]}
                            createSubscription={async (data, actions) => {
                              try {
                                if (!email || !email.includes('@')) {
                                  setError('Valid email is required');
                                  return;
                                }
                                const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
                                if (!password || !passwordRegex.test(password)) {
                                  setError('Password must be at least 8 characters and include at least one number and one special character (e.g., !@#$%^&*)');
                                  return;
                                }
                                if (!fullName) {
                                  setError('Full name is required');
                                  return;
                                }

                                console.log('Starting PayPal subscription creation with discountInfo:', discountInfo);

                                let planId = MEMBERSHIP_PLANS.ANNUAL.paypalPlanId;
                                if (discountInfo?.type === 'percentage') {
                                  planId = discountInfo.annualPlanId || MEMBERSHIP_PLANS.ANNUAL.discountedPlans[discountInfo.value]?.paypal;
                                } else if (activePromotion && activePromotion.appliesTo.includes(MEMBERSHIP_PLANS.ANNUAL.id)) {
                                  planId = MEMBERSHIP_PLANS.ANNUAL.discountedPlans[activePromotion.discountValue]?.paypal;
                                }

                                console.log('Using PayPal plan ID:', planId);

                                if (!planId) {
                                  throw new Error('Invalid plan configuration');
                                }

                                return actions.subscription.create({
                                  plan_id: planId,
                                  application_context: {
                                    shipping_preference: 'NO_SHIPPING',
                                    user_action: 'SUBSCRIBE_NOW',
                                  },
                                });
                              } catch (error) {
                                console.error('PayPal subscription failed:', error);
                                setError(error.message);
                                throw error;
                              }
                            }}
                            onApprove={(data) => {
                              const pendingSignup = {
                                email,
                                password,
                                fullName,
                                plan: MEMBERSHIP_PLANS.ANNUAL.id,
                                subscriptionID: data.subscriptionID,
                                discountCode: discountInfo ? discountCode : null,
                                discountInfo: discountInfo || null,
                                teamId: discountInfo?.teamId || null,
                                paymentMethod
                              };

                              sessionStorage.setItem('pendingSignup', JSON.stringify(pendingSignup));
                              localStorage.setItem('isAuthenticated', 'true');
                              navigate('/subscription-success');
                            }}
                            onError={(err) => {
                              console.error('PayPal error:', err);
                              setError('PayPal subscription failed. Please try again.');
                            }}
                            style={{ layout: 'horizontal' }}
                            disabled={!isFormValid}
                          />
                        ) : (
                          <StripeButton
                            plan={MEMBERSHIP_PLANS.ANNUAL}
                            discountInfo={discountInfo}
                            activePromotion={activePromotion}
                            email={email}
                            fullName={fullName}
                            password={password}
                            isFormValid={isFormValid}
                            setError={setError}
                            setLoading={setLoading}
                            navigate={navigate}
                          />
                        )}
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      height: '100%',
                      boxShadow: '0 2px 12px 0 rgba(0,0,0,0.1)',
                      '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' }
                    }}>
                      <MDBox p={2}>
                        <MDTypography variant="h6" fontWeight="medium">
                          {MEMBERSHIP_PLANS.MONTHLY.name}
                        </MDTypography>
                        <MDTypography variant="caption" color="success" fontWeight="medium" mb={2}>
                          {discountInfo && discountInfo.isTrialUpgrade
                            ? MEMBERSHIP_PLANS.TRIAL_UPGRADE.description
                            : 'Monthly access to all features. Cancel anytime.'}
                        </MDTypography>
                        <MDTypography variant="h4" my={1}>
                          {(discountInfo && (discountInfo.plans.includes(MEMBERSHIP_PLANS.MONTHLY.id) ||
                            discountInfo.plans.includes('all_plans') ||
                            discountInfo.isTrialUpgrade)) ||
                            (activePromotion && activePromotion.appliesTo.includes(MEMBERSHIP_PLANS.MONTHLY.id)) ? (
                            <>
                              <MDTypography
                                component="span"
                                variant="h4"
                                color="text"
                                sx={{ textDecoration: 'line-through', opacity: 0.5, mr: 1 }}
                              >
                                ${MEMBERSHIP_PLANS.MONTHLY.price}
                              </MDTypography>
                              ${getDiscountedPrice(MEMBERSHIP_PLANS.MONTHLY.price, MEMBERSHIP_PLANS.MONTHLY.id, discountInfo, activePromotion)}
                            </>
                          ) : (
                            `$${MEMBERSHIP_PLANS.MONTHLY.price}`
                          )}
                          <MDTypography variant="caption" ml={0.5}>/month</MDTypography>
                        </MDTypography>
                        {discountInfo && parseFloat(getDiscountedPrice(MEMBERSHIP_PLANS.MONTHLY.price, MEMBERSHIP_PLANS.MONTHLY.id, discountInfo, activePromotion)) === 0 && discountInfo.is3MonthGift ? (
                          <MDButton
                            variant="gradient"
                            color="success"
                            fullWidth
                            onClick={async () => {
                              try {
                                setLoading(true);
                                if (!email || !email.includes('@')) {
                                  setError('Valid email is required');
                                  return;
                                }
                                const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
                                if (!password || !passwordRegex.test(password)) {
                                  setError('Password must be at least 8 characters and include at least one number and one special character (e.g., !@#$%^&*)');
                                  return;
                                }
                                if (!fullName) {
                                  setError('Full name is required');
                                  return;
                                }
                                const auth = getAuth();

                                try {
                                  const methods = await fetchSignInMethodsForEmail(auth, email);
                                  if (methods.length > 0) {
                                    throw new Error('Email already registered');
                                  }
                                } catch (error) {
                                  if (error.code === 'auth/email-already-in-use') {
                                    setError('Email already registered');
                                    return;
                                  }
                                  throw error;
                                }

                                const result = await createUserWithEmailAndPassword(auth, email, password);

                                const userDocData = {
                                  fullName,
                                  email,
                                  plan: MEMBERSHIP_PLANS.MONTHLY.id,
                                  isGifted: true,
                                  is3MonthGift: true,
                                  subscriptionName: '3 Month Gifted Access',
                                  giftType: '3 Month Gifted Access',
                                  giftDuration: '3 months',
                                  convertToDemo: true,
                                  giftCode: discountCode,
                                  createdAt: new Date().toISOString(),
                                  expiresAt: new Date(Date.now() + (90 * 24 * 60 * 60 * 1000)).toISOString()
                                };

                                if (discountInfo?.teamId) {
                                  userDocData.profileInfo = {
                                    role: 'member',
                                    teamId: discountInfo.teamId,
                                    teamRole: 'member',
                                    teamPermissions: { [discountInfo.teamId]: false }
                                  };
                                  await updateDoc(doc(db, 'teams', discountInfo.teamId), {
                                    members: arrayUnion(result.user.uid)
                                  });
                                  sessionStorage.setItem('pendingTeamPermission', JSON.stringify({
                                    userId: result.user.uid,
                                    teamId: discountInfo.teamId
                                  }));
                                }

                                await setDoc(doc(db, 'subscriptions', result.user.uid), {
                                  userId: result.user.uid,
                                  type: 'monthly',
                                  status: 'active',
                                  createdAt: new Date().toISOString(),
                                  plan: MEMBERSHIP_PLANS.MONTHLY.id,
                                  isGifted: true,
                                  is3MonthGift: true,
                                  giftType: '3 Month Gifted Access',
                                  subscriptionName: '3 Month Gifted Access',
                                  convertToDemo: true,
                                  giftCode: discountCode,
                                  giftDuration: '3 months',
                                  planDetails: {
                                    cost: 0,
                                    originalCost: MEMBERSHIP_PLANS.MONTHLY.price,
                                    startDate: new Date().toISOString(),
                                    endDate: new Date(Date.now() + (90 * 24 * 60 * 60 * 1000)).toISOString(),
                                    convertToDemoAt: new Date(Date.now() + (90 * 24 * 60 * 60 * 1000)).toISOString(),
                                    discountApplied: {
                                      code: discountCode,
                                      value: 100,
                                      type: 'percentage',
                                      is3MonthGift: true,
                                      giftType: '3 Month Gifted Access'
                                    }
                                  }
                                });

                                await setDoc(doc(db, 'Users', result.user.uid), userDocData);

                                const pendingSignup = {
                                  email,
                                  password,
                                  fullName,
                                  plan: MEMBERSHIP_PLANS.MONTHLY.id,
                                  isGifted: true,
                                  is3MonthGift: true,
                                  subscriptionName: '3 Month Gifted Access',
                                  giftType: '3 Month Gifted Access',
                                  giftDuration: '3 months',
                                  convertToDemo: true,
                                  giftCode: discountCode,
                                  discountInfo: {
                                    ...discountInfo,
                                    giftType: '3 Month Gifted Access'
                                  },
                                  teamId: discountInfo?.teamId || null,
                                  paymentMethod
                                };

                                sessionStorage.setItem('pendingSignup', JSON.stringify(pendingSignup));
                                localStorage.setItem('isAuthenticated', 'true');
                                localStorage.setItem('userID', result.user.uid);

                                navigate('/authentication/sign-in');
                              } catch (error) {
                                console.error('Gift account creation failed:', error);
                                setError(error.message);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            disabled={!isFormValid}
                          >
                            Create 3-Month Gifted Account
                          </MDButton>
                        ) : paymentMethod === 'paypal' ? (
                          <PayPalButtons
                            forceReRender={[discountInfo, debouncedEmail, debouncedPassword, debouncedFullName, paymentMethod]}
                            createSubscription={async (data, actions) => {
                              try {
                                if (!email || !email.includes('@')) {
                                  setError('Valid email is required');
                                  return;
                                }
                                const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
                                if (!password || !passwordRegex.test(password)) {
                                  setError('Password must be at least 8 characters and include at least one number and one special character (e.g., !@#$%^&*)');
                                  return;
                                }
                                if (!fullName) {
                                  setError('Full name is required');
                                  return;
                                }

                                console.log('Starting PayPal subscription creation with discountInfo:', discountInfo);

                                let planId = MEMBERSHIP_PLANS.MONTHLY.paypalPlanId;
                                if (discountInfo?.type === 'percentage') {
                                  planId = discountInfo.monthlyPlanId || MEMBERSHIP_PLANS.MONTHLY.discountedPlans[discountInfo.value]?.paypal;
                                } else if (activePromotion && activePromotion.appliesTo.includes(MEMBERSHIP_PLANS.MONTHLY.id)) {
                                  planId = MEMBERSHIP_PLANS.MONTHLY.discountedPlans[activePromotion.discountValue]?.paypal;
                                }

                                console.log('Using PayPal plan ID:', planId);

                                if (!planId) {
                                  throw new Error('Invalid plan configuration');
                                }

                                return actions.subscription.create({
                                  'plan_id': planId,
                                  'application_context': {
                                    'shipping_preference': 'NO_SHIPPING',
                                    'user_action': 'SUBSCRIBE_NOW',
                                  },
                                });
                              } catch (error) {
                                console.error('PayPal subscription failed:', error);
                                setError(error.message);
                                throw error;
                              }
                            }}
                            onApprove={(data) => {
                              const pendingSignup = {
                                email,
                                password,
                                fullName,
                                plan: discountInfo?.isTrialUpgrade ? MEMBERSHIP_PLANS.TRIAL_UPGRADE.id : MEMBERSHIP_PLANS.MONTHLY.id,
                                subscriptionID: data.subscriptionID,
                                discountCode: discountInfo ? discountCode : null,
                                discountInfo: discountInfo || null,
                                teamId: discountInfo?.teamId || null,
                                paymentMethod
                              };

                              console.log('Pending Signup Data:', pendingSignup);
                              sessionStorage.setItem('pendingSignup', JSON.stringify(pendingSignup));
                              localStorage.setItem('isAuthenticated', 'true');
                              navigate('/subscription-success');
                            }}
                            onError={(err) => {
                              console.error('PayPal error:', err);
                              setError('PayPal subscription failed. Please try again.');
                            }}
                            style={{ layout: "horizontal" }}
                            disabled={!isFormValid}
                          />
                        ) : (
                          <StripeButton
                            plan={MEMBERSHIP_PLANS.MONTHLY}
                            discountInfo={discountInfo}
                            activePromotion={activePromotion}
                            email={email}
                            fullName={fullName}
                            password={password}
                            isFormValid={isFormValid}
                            setError={setError}
                            setLoading={setLoading}
                            navigate={navigate}
                          />
                        )}
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="h6" mt={2} mb={2}>
                      Try it First
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      height: '100%',
                      boxShadow: '0 2px 12px 0 rgba(0,0,0,0.1)',
                      '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' }
                    }}>
                      <MDBox p={2} sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="medium">
                            {MEMBERSHIP_PLANS.TRIAL.name}
                          </MDTypography>
                          <MDTypography variant="body2" color="text" mb={2}>
                            Full access for free for 7 days, no credit card required. Converts to demo account after trial period.
                          </MDTypography>
                        </MDBox>
                        <MDButton
                          variant="gradient"
                          color="success"
                          fullWidth
                          onClick={handleTrialSignup}
                          disabled={!isFormValid || loading}
                        >
                          {loading ? <CircularProgress size={20} color="inherit" /> : "Start 7-Day Free Trial"}
                        </MDButton>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      height: '100%',
                      boxShadow: '0 2px 12px 0 rgba(0,0,0,0.1)',
                      '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' }
                    }}>
                      <MDBox p={2} sx={{
                        height: '100%',
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="medium">
                            {MEMBERSHIP_PLANS.DEMO.name}
                          </MDTypography>
                          <MDTypography variant="body2" color="text" mb={2}>
                            Explore basic features with limited access including access to Trading View Advanced Charts for life.
                          </MDTypography>
                        </MDBox>
                        <MDButton
                          variant="gradient"
                          color="info"
                          fullWidth
                          onClick={handleDemoSignup}
                          disabled={!isFormValid}
                        >
                          Create Demo Account
                        </MDButton>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </MDBox>
    </Elements>
  );
}

export default SignUpPage;